import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { etapaPorId } from '@/lib/constants';
import { origemPorId } from '@/leads/origens';
import { formatarTelefone } from '@/lib/telefone';
import { buscarLeads } from '@/services/leadsService';

/**
 * "Vincular a lead existente" — escolhe um lead do funil para a conversa aberta.
 *
 * A busca vai ao banco (`buscarLeads`: telefone pelas variantes do 9º dígito,
 * nome por prefixo), porque o inbox não carrega a coleção de leads em memória.
 * Abre já procurando pelo telefone do contato: é o caso comum — a pessoa está
 * no funil com o número cadastrado à mão.
 *
 * Quem grava é a callable `vincularLeadConversa` (no `PainelContato`); aqui só
 * se escolhe. Sem `leadId` gravado pelo cliente: as rules proíbem.
 */
export default function DialogoVincularLead({ aberto, aoFechar, clinicaId, conversa, aoEscolher, ocupado, config = null }) {
  const [incompleta, setIncompleta] = useState(false); // ★ WT lote 1b
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState(null);

  // Ao abrir: termo = telefone do contato e busca imediata.
  useEffect(() => {
    if (!aberto) return;
    setTermo(conversa?.contatoTelefone ?? '');
    setResultados([]);
    setErro(null);
  }, [aberto, conversa?.id]);

  useEffect(() => {
    if (!aberto) return undefined;
    const texto = termo.trim();
    if (texto.length < 3) {
      setResultados([]);
      return undefined;
    }
    let cancelado = false;
    setIncompleta(false);
    setBuscando(true);
    const timer = setTimeout(async () => {
      try {
        const lista = await buscarLeads(clinicaId, texto);
        // ★★ WT 1b: a marca do serviço quando uma das consultas foi recusada.
        if (!cancelado) setIncompleta(Boolean(lista?.incompleta));
        if (!cancelado) setResultados(lista);
      } catch (e) {
        console.error('[OdontoSDR] Busca de leads falhou:', e);
        if (!cancelado) setErro('Não foi possível buscar leads.');
      } finally {
        if (!cancelado) setBuscando(false);
      }
    }, 300);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [aberto, termo, clinicaId]);

  return (
    <Dialogo titulo="Vincular a lead existente" aberto={aberto} aoFechar={aoFechar} largura={520}>
      <div className="formulario">
        <p className="dialogo-contexto">
          Conversa com <strong>{conversa?.contatoNomeSalvo || conversa?.contatoNome || 'contato'}</strong>
          {conversa?.contatoTelefone ? ` · ${formatarTelefone(conversa.contatoTelefone)}` : ''}
        </p>

        <div className="campo">
          <label htmlFor="dvl-termo">Buscar por telefone ou nome</label>
          <input
            id="dvl-termo"
            type="search"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="Telefone com DDD ou início do nome"
            autoFocus
          />
          <p className="campo-ajuda">
            Nome só encontra pelo começo (“Maria” acha “Maria Silva”, “Silva” não).
          </p>
        </div>

        {erro && <p className="erro">{erro}</p>}
        {buscando && <p className="indicador-nota">Buscando…</p>}
        {/* ⚠⚠ `!incompleta` é o que impede "Nenhum lead encontrado" de afirmar ausência
            quando uma das duas consultas da busca foi RECUSADA. */}
        {!buscando && termo.trim().length >= 3 && resultados.length === 0 && !incompleta && (
          <p className="vazio compacto">Nenhum lead encontrado.</p>
        )}
        {incompleta && (
          <p className="aviso-carga" role="alert">Resultados incompletos — recarregue a página.</p>
        )}

        <ul className="lista-leads-vincular">
          {resultados.map((lead) => {
            const etapa = etapaPorId(lead.etapa);
            return (
              <li key={lead.id}>
                <div>
                  <strong>{lead.nome || 'Sem nome'}</strong>
                  <span className="indicador-nota">
                    {lead.telefone ? formatarTelefone(lead.telefone) : 'sem telefone'}
                    {' · '}
                    {origemPorId(config, lead.origem).label}
                  </span>
                </div>
                <span className="pilula pequena" style={{ background: etapa.cor }}>
                  {etapa.label}
                </span>
                <button
                  type="button"
                  className="btn-primario compacto"
                  disabled={ocupado}
                  onClick={() => aoEscolher(lead)}
                >
                  Vincular
                </button>
              </li>
            );
          })}
        </ul>

        <div className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={aoFechar}>
            Cancelar
          </button>
        </div>
      </div>
    </Dialogo>
  );
}
